import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import queryString from "query-string";
import { sum, trim, find } from "lodash";
import {
  Stack,
  FormLabel,
  FormControl,
  useBreakpointValue,
  InputGroup,
  FormHelperText,
  VStack,
  Center,
  Link,
  Checkbox,
  Box,
  Text,
} from "@chakra-ui/react";

import {
  createGroup,
  createEntry,
  fetchEntriesByQuestionGroupIdAndEmail,
} from "../api/Api";
import { StyledInput } from "../styled-components/StyledInput";
import { StyledButton } from "../styled-components/StyledButton";
import { StyledInputAddon } from "../styled-components/StyledInputAddon";
import { StyledTextarea } from "../styled-components/StyledTextarea";
import { StyledSelect } from "../styled-components/StyledSelect";
import { AwardDistributionInput } from "../components/AwardDistributionInput";
import { emailRegex, numberRegex } from "../utils/InputUtils";
import { IGroupInput, IEntryInput } from "../interfaces/Inputs";
import { IEntryWithGroup } from "../interfaces/Models";

interface IProps {
  questionGroupId: string;
  questionGroupName?: string;
  defaultGroupId?: string;
  defaultEmail?: string;
}

export const CreateGroupForm = (props: IProps) => {
  const navigate = useNavigate();

  const inputSize = useBreakpointValue(["sm", "md"]);
  const formControlWidth = ["sm", "xl"];

  const [name, setName] = useState("");
  const [email, setEmail] = useState(props.defaultEmail || "");
  const isEmailInvalid = email && !emailRegex.test(email);

  // Copy picks state
  const [existingEntries, setExistingEntries] = useState<IEntryWithGroup[]>([]);
  const [selectedExistingEntryId, setSelectedExistingEntryId] =
    useState<string>("");
  const [copyPicksChecked, setCopyPicksChecked] = useState(true);
  const [entryName, setEntryName] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Debounced fetch of existing entries when email changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!email || !emailRegex.test(email)) {
      setExistingEntries([]);
      setSelectedExistingEntryId("");
      setEntryName("");
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      const { data, hasError } = await fetchEntriesByQuestionGroupIdAndEmail(
        props.questionGroupId,
        trim(email)
      );
      if (!hasError && data?.length > 0) {
        setExistingEntries(data);
        // Default to entry matching defaultGroupId, or first entry
        const defaultEntry = props.defaultGroupId
          ? find(data, (e) => e.groupId === props.defaultGroupId)
          : null;
        const initialEntry = defaultEntry || data[0];
        setSelectedExistingEntryId(initialEntry.id);
        setEntryName(initialEntry.name);
        setCopyPicksChecked(true);
      } else {
        setExistingEntries([]);
        setSelectedExistingEntryId("");
        setEntryName("");
      }
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [email, props.questionGroupId, props.defaultGroupId]);

  const [notes, setNotes] = useState("");
  const [entryFee, setEntryFee] = useState("0");
  const [entryFeeInstructions, setEntryFeeInstructions] = useState("");
  const [awardDistributions, setAwardDistributions] = useState([
    "100",
    "0",
    "0",
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);

  const setAwardDistributionForIndex = (index: number) => {
    return (value: string) => {
      const newAwardDistributions = [...awardDistributions];
      newAwardDistributions[index] = value;
      setAwardDistributions(newAwardDistributions);
    };
  };

  const numericAwardDistributions = awardDistributions.map((strNum) =>
    parseFloat(strNum) ? parseFloat(strNum) : 0
  );

  const isAwardDistributionsInvalid = sum(numericAwardDistributions) !== 100;

  const isCopyPicksActive = copyPicksChecked && existingEntries.length > 0;

  const isSubmitDisabled = Boolean(
    !name ||
      (Number(entryFee) && isAwardDistributionsInvalid) ||
      !emailRegex.test(email) ||
      (isCopyPicksActive && !trim(entryName))
  );

  const onSubmit = async () => {
    setIsSubmitting(true);

    const groupInput: IGroupInput = {
      name: trim(name),
      notes,
      email: trim(email),
      questionGroupId: props.questionGroupId,
      entryFee: parseFloat(entryFee),
      entryFeeInstructions,
      awardDistributions: numericAwardDistributions,
    };

    const { data: group, hasError } = await createGroup(groupInput);

    if (hasError) {
      setIsSubmitting(false);
      setHasApiError(true);
      return;
    }

    // Copy picks from existing entry if checked
    if (
      copyPicksChecked &&
      selectedExistingEntryId &&
      existingEntries.length > 0
    ) {
      const selectedEntry = find(
        existingEntries,
        (e) => e.id === selectedExistingEntryId
      );
      if (selectedEntry) {
        try {
          const entryInput: IEntryInput = {
            name: trim(entryName),
            email: trim(email),
            groupId: group.id,
            answers: selectedEntry.answers,
          };
          await createEntry(entryInput, group.id, group.password);
        } catch (e) {
          // Entry creation failed — still navigate, user can submit manually on the group page
          console.error("Failed to copy entry picks:", e);
        }
      }
    }

    const groupUrlId = group.urlId;
    const groupPassword = group.password;
    const qsDict = {
      code: groupPassword,
    };
    const qs = queryString.stringify(qsDict);

    setIsSubmitting(false);
    setHasApiError(false);
    navigate(`/group/${groupUrlId}?${qs}`, {
      state: {
        showOnGroupCreateModal: true,
      },
    });
  };

  return (
    <Stack spacing={[3, 5]} width="100%" align="center">
      <FormControl isRequired maxWidth={formControlWidth} px="10px">
        <FormLabel>Group Name</FormLabel>
        <StyledInput
          variant="filled"
          size={inputSize}
          value={name}
          onChange={(event) => setName(event.target.value)}
          colorScheme="gray"
        />
      </FormControl>
      <FormControl isRequired maxWidth={formControlWidth} px="10px">
        <FormLabel>Email</FormLabel>
        <StyledInput
          type="email"
          colorScheme="gray"
          variant="filled"
          size={inputSize}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          isInvalid={isEmailInvalid}
        />
        {isEmailInvalid && (
          <FormHelperText color="red.300">
            Please enter a valid email.
          </FormHelperText>
        )}
      </FormControl>
      <FormControl maxWidth={formControlWidth} px="10px">
        <FormLabel>Notes</FormLabel>
        <StyledTextarea
          height={["5.4rem", "5.65rem"]}
          colorScheme="gray"
          variant="filled"
          size={inputSize}
          placeholder="Enter any notes you want your participants to see. This will show up on the top of your group page."
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </FormControl>
      <FormControl maxWidth={formControlWidth} px="10px">
        <FormLabel>Entry Fee</FormLabel>
        <FormHelperText
          color="whiteAlpha.600"
          fontSize={["xs", "sm"]}
          mt="-6px"
          mb="6px"
        >
          Optional. You handle collection and payouts (e.g. Venmo).
        </FormHelperText>
        <InputGroup size={inputSize}>
          <StyledInputAddon
            colorScheme="gray"
            placement="left"
            children="$"
            border="0"
          />
          {/* TODO: Why does this min not work? */}
          <StyledInput
            colorScheme="gray"
            variant="filled"
            type="number"
            pattern={numberRegex.source}
            value={entryFee}
            onChange={(event) => setEntryFee(event.target.value)}
            onBlur={(event) =>
              event.target.value === "" ? setEntryFee("0") : null
            }
            borderLeftRadius="0"
          />
        </InputGroup>
      </FormControl>
      {parseFloat(entryFee) && (
        <>
          <FormControl maxWidth={formControlWidth} px="10px">
            <FormLabel>Entry Fee Instructions</FormLabel>
            <StyledTextarea
              height={["5.4rem", "5.65rem"]}
              colorScheme="gray"
              variant="filled"
              size={inputSize}
              placeholder="How should people pay the entry fee? (e.g. Venmo @username or PayPal name@email.com)"
              value={entryFeeInstructions}
              onChange={(event) => setEntryFeeInstructions(event.target.value)}
            />
          </FormControl>
          <FormControl maxWidth={formControlWidth} px="10px">
            <FormLabel>Award Distribution</FormLabel>
            <VStack>
              {["1st", "2nd", "3rd"].map((label, index) => {
                return (
                  <AwardDistributionInput
                    key={index}
                    label={label}
                    value={awardDistributions[index]}
                    setAwardDistribution={setAwardDistributionForIndex(index)}
                    inputSize={inputSize}
                  />
                );
              })}
            </VStack>
            {isAwardDistributionsInvalid && (
              <FormHelperText color="red.300">
                All percents must add up to 100.
              </FormHelperText>
            )}
          </FormControl>
        </>
      )}
      {existingEntries.length > 0 && (
        <Box
          maxWidth={formControlWidth}
          px="10px"
          pt={["8px", "12px"]}
          w="100%"
        >
          <Box
            py={["8px", "12px"]}
            px="10px"
            borderWidth="1px"
            borderColor="brandColorScheme.300"
            borderRadius="md"
          >
            <Text
              fontSize={["sm", "md"]}
              fontWeight="bold"
              color="brandColorScheme.300"
              mb={["4px", "8px"]}
            >
              You already have picks for{" "}
              {props.questionGroupName || "this event"}!
            </Text>
            <Checkbox
              isChecked={copyPicksChecked}
              onChange={(e) => setCopyPicksChecked(e.target.checked)}
              colorScheme="brandColorScheme"
              size={inputSize}
              alignItems="flex-start"
              sx={{ "& .chakra-checkbox__control": { mt: "3px" } }}
            >
              <Text fontSize={["sm", "md"]}>
                Auto-create my entry, copy picks from
                {existingEntries.length === 1 && (
                  <Text as="span" fontWeight="bold">
                    {" "}
                    {existingEntries[0].groupName}
                  </Text>
                )}
              </Text>
            </Checkbox>
            {existingEntries.length > 1 && (
              <Box mt={["4px", "8px"]} pl={["24px", "28px"]}>
                <StyledSelect
                  colorScheme="gray"
                  size={inputSize}
                  value={selectedExistingEntryId}
                  onChange={(e) => {
                    const prevEntry = find(
                      existingEntries,
                      (ent) => ent.id === selectedExistingEntryId
                    );
                    setSelectedExistingEntryId(e.target.value);
                    const newEntry = find(
                      existingEntries,
                      (ent) => ent.id === e.target.value
                    );
                    if (
                      newEntry &&
                      (!prevEntry || entryName === prevEntry.name)
                    ) {
                      setEntryName(newEntry.name);
                    }
                  }}
                >
                  {existingEntries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.groupName}
                    </option>
                  ))}
                </StyledSelect>
              </Box>
            )}
            {copyPicksChecked && (
              <Box mt={["4px", "8px"]} pl={["24px", "28px"]}>
                <Text fontSize={["xs", "sm"]} mb="4px" fontWeight="medium">
                  Entry Name
                </Text>
                <StyledInput
                  colorScheme="gray"
                  variant="filled"
                  size={inputSize}
                  value={entryName}
                  onChange={(e) => setEntryName(e.target.value)}
                  placeholder="Your display name"
                  isInvalid={!trim(entryName)}
                />
                {!trim(entryName) && (
                  <Text color="red.300" fontSize={["xs", "sm"]} mt="4px">
                    Please enter a name for your entry.
                  </Text>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      <FormControl maxWidth={formControlWidth} px="10px">
        <Center>
          <VStack pt={["10px", "20px"]}>
            <StyledButton
              colorScheme="brandColorScheme"
              size={inputSize}
              isDisabled={isSubmitDisabled}
              isLoading={isSubmitting}
              loadingText={"Creating 🎉"}
              onClick={onSubmit}
            >
              Create 🎉
            </StyledButton>
            {hasApiError && (
              <FormHelperText px="10px" color="red.300">
                Something went wrong. Please try again or contact{" "}
                <Link
                  target="_blank"
                  color="red.500"
                  href="mailto:hello@sbpropbets.com"
                  textDecoration="underline"
                >
                  hello@sbpropbets.com
                </Link>
                .
              </FormHelperText>
            )}
          </VStack>
        </Center>
      </FormControl>
    </Stack>
  );
};
